import { BaseBuilder, BaseBuilderOptions, IBuilder } from './BaseBuilder.ts';
import { ProductCriteriaOptions } from '@services/MarppleShop/features/ProductList/type/product-list.request.ts';
import { CommonPageType, CommonRankType, CommonFilters } from './shared/common-types';
import { PageManager, RankManager, FilterManager, SortManager } from './managers';

export type EbookPage = CommonPageType;
export type EbookRankType = CommonRankType;
export type EbookBuilderFilters = CommonFilters;

export interface EbookBuilderOptions extends BaseBuilderOptions {
  page?: EbookPage;
  filters?: EbookBuilderFilters;
  sort?: ProductCriteriaOptions['sort'];
  rankType?: EbookRankType;
}

/**
 * 전자책 전용 URL 빌더
 */
export class EbookBuilder implements IBuilder {
  private baseBuilder: BaseBuilder;

  // Composition: 각 매니저가 독립적인 책임을 가짐
  private pageManager: PageManager<EbookPage>;
  private rankManager: RankManager<EbookRankType>;
  private filterManager: FilterManager<EbookBuilderFilters>;
  private sortManager: SortManager;

  // string 타입 기반 URL 매핑
  private static readonly PAGE_URL_MAP: Record<EbookPage, string> = {
    RANKING: '/:lang/@/ebooks/ranking',
    PRODUCTS: '/:lang/@/ebooks/products',
  };

  constructor(initialOptions: EbookBuilderOptions) {
    this.baseBuilder = new BaseBuilder(initialOptions);

    this.pageManager = new PageManager(initialOptions.page, 'RANKING', EbookBuilder.PAGE_URL_MAP);

    this.rankManager = new RankManager(initialOptions.rankType, 'REALTIME');

    this.filterManager = new FilterManager(initialOptions.filters, {});

    this.sortManager = new SortManager(initialOptions.sort, 'popular');
  }

  private getBaseUrl(): string {
    const lang = this.baseBuilder.getLangOrThrow();
    // PageManager가 URL 생성을 담당
    return this.pageManager.getPageUrl(lang);
  }

  setPage(page: EbookPage): this {
    this.pageManager.setPage(page);
    return this;
  }

  setRankType(rankType: EbookRankType): this {
    this.rankManager.setRankType(rankType);
    return this;
  }

  setFilters(filters: Partial<EbookBuilderFilters>): this {
    this.filterManager.setFilters(filters);
    return this;
  }

  setSort(sort: ProductCriteriaOptions['sort']): this {
    this.sortManager.setSort(sort);
    return this;
  }

  build(): string {
    const baseUrl = this.getBaseUrl();

    if (this.pageManager.isRanking()) {
      return this.rankManager.buildRankingUrl(baseUrl);
    } else if (this.pageManager.isProducts()) {
      return this.filterManager.buildProductsUrl(baseUrl, this.sortManager.getSort());
    } else {
      return baseUrl;
    }
  }

  clone(): EbookBuilder {
    return new EbookBuilder({
      lang: this.baseBuilder.getLangOrThrow(),
      cate_root_id: this.baseBuilder.getCateRoot().id,
      page: this.pageManager.getPage(),
      filters: this.filterManager.getFilters(),
      sort: this.sortManager.getSort(),
      rankType: this.rankManager.getRankType(),
    });
  }
}
